import React, { useContext } from 'react';
import { Menu } from '@base-ui-components/react/menu';
import { MenuSelectContext } from './DropdownButton';

/**
 * Item inside a ui DropdownButton menu.
 *
 * @param {object} props
 * @param {*} [props.eventKey] Passed to onSelect handlers on activation.
 * @param {(eventKey: any, event: Event) => void} [props.onSelect] Per-item
 *   handler; the parent DropdownButton's onSelect also fires.
 * @param {boolean} [props.divider] Render a separator rule instead of an item.
 * @param {boolean} [props.header] Render a non-interactive section header.
 * @param {boolean} [props.disabled] Not selectable or keyboard-focusable.
 * @param {boolean} [props.active] Highlighted (selected) appearance.
 * @param {string} [props.href] Render as a link; target='_blank' gets
 *   rel=noopener automatically.
 */
const MenuItem = ({
  eventKey,
  onSelect,
  onClick,
  divider,
  header,
  disabled,
  active,
  href,
  target,
  className,
  children,
  ...rest
}) => {
  const parentOnSelect = useContext(MenuSelectContext);

  if (divider) {
    return <Menu.Separator className="ui-menu__divider" role="separator" />;
  }

  if (header) {
    return (
      <div className={['ui-menu__header', className].filter(Boolean).join(' ')} {...rest}>
        {children}
      </div>
    );
  }

  const handleClick = (event) => {
    if (onClick) onClick(event);
    if (onSelect) onSelect(eventKey, event);
    if (parentOnSelect) parentOnSelect(eventKey, event);
  };

  const classes = ['ui-menu__item', active && 'active', className].filter(Boolean).join(' ');

  return (
    <Menu.Item
      className={classes}
      disabled={disabled}
      onClick={handleClick}
      render={href ? <a href={href} target={target} rel={target === '_blank' ? 'noopener noreferrer' : undefined} /> : undefined}
      {...rest}
    >
      {children}
    </Menu.Item>
  );
};

export default MenuItem;
